<template>
  <div class="container-fluid">
    <TopSection
      :title="$t('user_data') | capitalize"
      :section="$t('users')"
      :sub-section="$t('userProfile')"
      back-section="users-list"
    >
      <template v-slot:actions>
        <span
          class="btn btn-outline-primary px-4 p-2 mr-2"
          @click="goToUsersList"
        >
          <span>{{ $t("cancel") | capitalize }}</span>
        </span>

        <span
          v-if="isSuperUser"
          class="btn btn-primary px-4 p-2 ml-2"
          @click="onSubmit"
        >
          <spinner v-if="waiting" />
          <span v-else>{{ $t("save") | capitalize }}</span>
        </span>
      </template>
    </TopSection>

    <div class="row pt-4">
      <div class="col-2">
        <div class="text-center justify-center">
          <Avatar
            v-if="username"
            :username="username"
            :size="132"
          />
        </div>
      </div>

      <div class="col-4">
        <!-- Full Name -->
        <div>
          {{ $t("name_and_last_name") | capitalize }}
        </div>
        <input
          v-model="name_and_last_name"
          minlength="3"
          maxlength="30"
          class="form-control"
        />
        <!-- Email -->
        <div class="py-2">
          {{ $t("email") | capitalize }}
        </div>

        <input
          v-model="email"
          class="form-control"
          type="email"
          disabled
        />

        <!-- Organization -->
        <div class="py-2">
          {{ $t("organization") | capitalize }}
        </div>

        <input
          v-model="member.username"
          class="form-control"
          disabled
        />
      </div>

      <div
        v-if="isSuperUser"
        class="col-4"
      >
        <!-- Status -->
        <div>
          {{ $t("status") | capitalize }}
        </div>
        <Multiselect
          v-model="status"
          class="clickable"
          :options="[statusTypes.ACTIVE, statusTypes.BLOCKED]"
          :hide-selected="true"
          :show-labels="false"
        >
          <template v-slot:singleLabel="{ option }">
            <div class="form_block2 societies space-between">
              <div style="width:20px;">
                <i :class="statusColor(option)" class="fa fa-circle"/>
              </div>
              <div class="option__desc">
                <span class="option__title">{{ option | capitalize }}</span>
              </div>
            </div>
          </template>

          <template v-slot:option="{ option }">
            <div class="form_block2 societies space-between">
              <div style="width:20px;">
                <i :class="statusColor(option)" class="fa fa-circle"/>
              </div>
              <div class="option__desc">
                <span class="option__title">{{ option | capitalize }}</span>
              </div>
            </div>
          </template>
        </Multiselect>

        <!-- Notify -->
        <div class="py-2">
          {{ $t("notifications") | capitalize }}
        </div>
        <b-form-checkbox
          v-model="notify"
          name="notify"
          size="lg"
        >
          {{ $t("send_notifications") }}
        </b-form-checkbox>
        <div class="py-2 ">
          <span v-if="notify">{{ $t("this_user_will_receive_all_notifications") }}</span>
          <span v-else>{{ $t("this_user_will_receive_no_notifications") }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import "../../../assets/styles/libs";

.delete-modal {
  padding: 0 280px;
}

.multiselect {
  min-width: auto !important;
}

.delete-icon {
  position: absolute;
  top: -25px;
  display: inline;
  right: 20px;

  @media screen and (max-width: $break-large) {
    display: none !important;
  }
}

input {
  height: 48px;
  padding: 6px;
  color: black;
  border: 1px solid #c0c5cf;
  border-radius: 4px;

  &:invalid {
    border: 2px solid red !important;
  }
}
</style>


<script>
import { isDefinedString, validateEmail } from "@/assets/libs/utils"
import { ApiCalls } from "@/assets/libs/services"
import { BASIC } from "@/store/modules/consts"
import { mapGetters } from "vuex"

export default {
  name: "UsersDetail",
  data () {
    return {
      waiting: false,
      initialStatus: "",
      status: "",
      debug: false,
      username: "",
      email: "",
      notify: false,
      user_type: "",
      name_and_last_name: "",
      member: "",
      show: true,
    }
  },
  computed: {
    ...mapGetters("soundsys", ["isSuperUser", "statusTypes", "orgTypes"]),
  },
  methods: {
    statusColor (state) {
      return state + "_state"
    },
    isMlcMember (member) {
      try {
        return member.org_type === this.orgTypes.MLC
      } catch (err) {
        return false
      }
    },
    actionClass (expected) {
      let out = ""

      if (this.status !== expected) {
        out += "clear"
      }
      return out
    },
    async saveStatus () {
      if (this.status !== this.initialStatus) {
        // Activate
        if (this.status === this.statusTypes.ACTIVE) {
          return ApiCalls.activateUser(this.id)
        }
        // Dectivate
        if (this.status === this.statusTypes.BLOCKED) {
          return ApiCalls.blockUser(this.id)
        }
      } else {
        return new Promise((resolve) => resolve())
      }
    },
    validate () {
      if (!isDefinedString(this.name_and_last_name)) {
        this.$toasted.global.toast(this.$t("name_and_last_name_not_defined"))
        return false
      } else if (!isDefinedString(this.email)) {
        this.$toasted.global.toast(this.$t("email_not_defined"))
      } else if (!validateEmail(this.email)) {
        this.$toasted.global.toast(this.$t("email_format_not_valid"))
        return false
      } else if (!isDefinedString(this.member) || this.member === null) {
        this.$toasted.global.toast(this.$t("member_not_defined"))
        return false
      } else if (
        this.isMlcMember(this.member) &&
        !isDefinedString(this.user_type)
      ) {
        this.$toasted.global.toast(this.$t("user_type_not_defined"))
        return false
      } else {
        return true
      }
    },
    async onSubmit () {
      if (!this.validate()) return

      this.waiting = true

      const data = {
        id: this.id,
        username: this.username,
        email: this.email,
        notify: this.notify,
        user_type: this.isMlcMember(this.member) ? this.user_type : BASIC,
        status: this.status,
        name_and_last_name: this.name_and_last_name,
        mode: "user_info",
      }

      if (this.member !== null) {
        data["member"] = this.member.id
      }

      try {
        await this.saveStatus()
        await ApiCalls.save_user(data)
        this.waiting = false
        this.goToUsersList()
      } catch (error) {
        this.waiting = false
        this.status = this.initialStatus
        this.$toasted.global.toast(error)
      }
      
    },
    mapData (data) {
      /* initial data when edit an user */
      this.username = data.username
      this.email = data.profile.email
      this.notify = data.profile.notify
      this.member = data.profile.member
      this.user_type = data.profile.user_type
      this.status = data.profile.status
      this.initialStatus = data.profile.status
      this.name_and_last_name = data.profile.name_and_last_name
    },
    goToUsersList () {
      const member_id = this.$route.params.member_id
      if(!member_id) {
        this.$router.push({ "name": "users-list" })
      } else {
        this.$router.push({ name: "members-users-list", params: { member_id } })
      }
    },
  },
  mounted () {
    if (this.id) {
      ApiCalls.getUserDetail(this.id).then(resp => {
        this.mapData(resp)
      })
    }
  },
  props: {
    id: Number,
  },
}
</script>

